import { useEffect, useMemo, useState } from 'react';
import { describeApiError } from '../qrs/errors';
import { useDebounced } from '../qrs/hooks';
import { fetchStylePreview } from './binaryApi';
import { localPrecheck } from './contrast';
import type { QrStyle, StyleIssue } from './types';

export const PREVIEW_DEBOUNCE_MS = 400;

export interface StylePreviewState {
  imageUrl: string | null;
  loading: boolean;
  error: string | null;
  /** Issues del servidor (header `X-Style-Issues`) o, mientras tanto, los calculados en el navegador. */
  issues: StyleIssue[];
}

/** Vista previa en vivo con debounce de 400 ms vía `POST /v1/qr-styles/preview` (R13.2). */
export function useStylePreview(style: QrStyle, orgId: string | undefined, data?: string): StylePreviewState {
  const key = JSON.stringify({ style, data });
  const debouncedKey = useDebounced(key, PREVIEW_DEBOUNCE_MS);
  const localIssues = useMemo(() => localPrecheck(style), [style]);
  const [state, setState] = useState<Omit<StylePreviewState, 'issues'> & { issues: StyleIssue[] | null }>({
    imageUrl: null,
    loading: true,
    error: null,
    issues: null,
  });

  useEffect(() => {
    const { style: s, data: d } = JSON.parse(debouncedKey) as { style: QrStyle; data?: string };
    const controller = new AbortController();
    let objectUrl: string | null = null;
    setState((prev) => ({ ...prev, loading: true, error: null }));
    fetchStylePreview(s, d, orgId, controller.signal)
      .then(({ blob, issues }) => {
        if (controller.signal.aborted) return;
        objectUrl = typeof URL.createObjectURL === 'function' ? URL.createObjectURL(blob) : null;
        setState({ imageUrl: objectUrl, loading: false, error: null, issues });
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setState({ imageUrl: null, loading: false, error: describeApiError(err, 'No pudimos generar la vista previa.'), issues: null });
      });
    return () => {
      controller.abort();
      if (objectUrl && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(objectUrl);
    };
  }, [debouncedKey, orgId]);

  const stale = key !== debouncedKey;
  return {
    imageUrl: state.imageUrl,
    loading: state.loading || stale,
    error: state.error,
    issues: stale || state.issues === null ? localIssues : state.issues,
  };
}
